import AccessoryCategoriesApi from '@/api/accessory-categories';
import AccessoryCategoryModel from '../models/accessory-category';

import arrayToTree from 'array-to-tree';

const getInitialParams = () => ({
  skip: 0,
  limit: 100,
  only_deleted: false,
  order: {
    column: 'title',
    desc: true,
  },
});

export default {
  namespaced: true,

  state: {
    selectedCategories: [],
    flatCategories: [],
    treeCategories: [],
    meta: {},
    loading: false,
    error: null,
    params: getInitialParams(),
    currentItem: null,
  },

  actions: {
    async fetch(ctx) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await AccessoryCategoriesApi.fetch(ctx.getters.params);

        const flatCategories = response.data.data.items;

        const treeCategories = arrayToTree(flatCategories, {
          parentProperty: 'parent_id',
        });

        ctx.commit('setFlatCategories', flatCategories);
        ctx.commit('setTreeCategories', treeCategories);
        ctx.commit('setMeta', response.data.meta);
      } catch (err) {
        ctx.commit('setError', err);
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async fetchOne(ctx, id) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await AccessoryCategoriesApi.fetchOneCategory(id);
        ctx.commit('setCurrentItem', new AccessoryCategoryModel(response.data.data));
      } catch (err) {
        ctx.commit('setError', err);
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async save(ctx, data) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await AccessoryCategoriesApi.updateCategory(data);
        ctx.commit('setCurrentItem', response.data.data);
        return null;
      } catch (err) {
        ctx.commit('setError', err);
        return err;
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async remove(ctx, data) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        await AccessoryCategoriesApi.removeCategory(data);
        return null;
      } catch (err) {
        ctx.commit('setError', err);
        return err;
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async create(ctx, data) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await AccessoryCategoriesApi.createCategory(data);
        ctx.commit('setCurrentItem', response.data.data);
        return null;
      } catch (err) {
        ctx.commit('setError', err);
        return err;
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async changeParams(ctx, params, reset) {
      if (reset) {
        ctx.commit('changeParams', {
          ...getInitialParams(),
          ...params,
        });
      } else {
        ctx.commit('changeParams', params);
      }
    },

    clearCurrentItem(ctx) {
      ctx.commit('clearCurrentItem');
    },

    changeSelectedCategories(ctx, selected) {
      ctx.commit('setSelectedCategories', selected);
    },
  },

  mutations: {
    setLoading(state, value) {
      state.loading = value;
    },

    setError(state, err) {
      state.error = err;
    },

    setSelectedCategories(state, data) {
      state.selectedCategories = data;
    },

    setFlatCategories(state, data) {
      state.flatCategories = data;
    },

    setTreeCategories(state, data) {
      state.treeCategories = data;
    },

    setMeta(state, meta) {
      state.meta = meta;
    },

    changeParams(state, params) {
      Object.assign(state.params, params);
    },

    setCurrentItem(state, data) {
      state.currentItem = data;
    },

    clearCurrentItem(state, data) {
      state.currentItem = null;
    },
  },

  getters: {
    meta: state => state.meta,
    statuses: state => state.statuses,
    selectedCategories: state => state.selectedCategories,
    flatCategories: state => state.flatCategories,
    treeCategories: state => state.treeCategories,
    availabilityStatuses: state => state.availabilityStatuses,
    error: state => state.error,
    inlineError: state => state.inlineError,
    params: (state, getters) => {
      return {
        ...state.params,
        search_string: getters.searchQuery,
      };
    },
    loading: state => state.loading,
    inlineLoading: state => state.inlineLoading,
    searchQuery: (state, getters, rootState, rootGetters) => rootGetters['util/searchQuery'],
    currentItem: state => state.currentItem,
    currentCategoryItem: state => state.currentCategoryItem,
  },
};
